<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import type { FormFieldErrors as Errors } from './internal/context';
import { computed, useId, useSlots } from 'vue';
import { injectFieldControl, provideControlDefaults, provideFieldControl } from '@/components/base/_internal/control';
import { cn } from '@/utils/shadcn/lib/utils';
import { injectReserveDescription, normalizeErrors, provideFormRow, provideReserveDescription } from './internal/context';
import FormFieldErrors from './internal/FormFieldErrors.vue';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = withDefaults(defineProps<{
    legend?: string
    description?: string
    errors?: Errors
    /** `section` titles a part of a form; `label` names one answer given with several controls. */
    variant?: 'section' | 'label'
    /**
     * `side` puts a `label` legend in the label column beside the controls, in line with the
     * side-labelled fields around it, and above them again below 30rem of the fieldset's width.
     * A `section` always stacks.
     */
    layout?: 'stack' | 'side'
    /** Label column for the side-labelled fields inside, so they line up. */
    labelWidth?: string
    required?: boolean
    disabled?: boolean
    class?: HTMLAttributes['class']
}>(), {
    legend: '',
    description: '',
    errors: null,
    variant: 'section',
    layout: 'stack',
    labelWidth: undefined,
    required: false,
    disabled: false,
    class: '',
});

const slots = useSlots();

const baseId = useId();
const descriptionId = `${baseId}-description`;
const errorsId = `${baseId}-errors`;

const errorList = computed(() => normalizeErrors(props.errors));
const hasLegend = computed(() => !!(props.legend || slots.legend));
const hasDescription = computed(() => !!(props.description || slots.description));
const describedBy = computed(() => [
    hasDescription.value && descriptionId,
    errorList.value.length > 0 && errorsId,
].filter(Boolean).join(' ') || undefined);

const layout = computed(() => (props.variant === 'label' ? props.layout : 'stack'));

const parent = injectFieldControl();
const disabled = computed(() => props.disabled || !!parent?.state.value.disabled);

// Only `disabled` reaches the fields inside; each field names its own control and errors.
provideFieldControl({
    state: computed(() => ({
        invalid: false,
        disabled: disabled.value,
        required: false,
    })),
    reportWidth: () => {},
});
provideControlDefaults({ legacyWidth: false });
provideFormRow(false);

const reserveDescription = injectReserveDescription();
provideReserveDescription(false);
</script>

<template>
    <fieldset
        data-slot="fieldset"
        :data-variant="props.variant"
        :data-layout="layout"
        :data-disabled="disabled || undefined"
        :disabled="disabled || undefined"
        :aria-describedby="describedBy"
        :aria-invalid="errorList.length > 0 || undefined"
        :class="cn('sph-fieldset group', props.class)"
        :style="props.labelWidth ? { '--form-field-label-width': props.labelWidth } : undefined"
    >
        <legend
            v-if="hasLegend"
            data-slot="fieldset-legend"
            class="sph-fieldset__legend"
        >
            <slot name="legend">
                {{ props.legend }}
            </slot><span
                v-if="props.required"
                aria-hidden="true"
            >*</span>
        </legend>

        <p
            v-if="hasDescription"
            :id="descriptionId"
            data-slot="fieldset-description"
            class="sph-fieldset__description"
        >
            <slot name="description">
                {{ props.description }}
            </slot>
        </p>
        <p
            v-else-if="reserveDescription && hasLegend && props.variant === 'section'"
            aria-hidden="true"
            class="sph-fieldset__description"
        >
            &nbsp;
        </p>

        <div
            data-slot="fieldset-content"
            class="sph-fieldset__content"
        >
            <slot></slot>
        </div>

        <FormFieldErrors
            v-if="errorList.length > 0"
            :id="errorsId"
            :errors="errorList"
            class="sph-fieldset__errors"
        />
    </fieldset>
</template>

<style scoped>
@reference '@/assets/styles/config.css';

/* A fieldset's min-width defaults to its content's, which lets a wide table push the form out of its column. */
.sph-fieldset {
    /* Under the legend, or under the description when there is one: the content starts the same distance below either. */
    --fieldset-header-gap: calc(var(--spacing) * 4);
    --fieldset-description-gap: calc(var(--spacing) * 1);

    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
}

.sph-fieldset__legend {
    float: left;
    width: 100%;
    padding: 0;
    margin-bottom: var(--fieldset-header-gap);

    @apply text-lg leading-tight font-semibold text-foreground;

    /* The description clears the float, which ignores a margin of its own above it, so the legend holds the gap. */
    &:has(+ .sph-fieldset__description) {
        margin-bottom: var(--fieldset-description-gap);
    }

    & + * {
        clear: left;
    }
}

.sph-fieldset__description {
    margin-bottom: var(--fieldset-header-gap);

    @apply text-sm leading-normal text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:text-primary;
}

.sph-fieldset__content {
    display: flex;
    flex-direction: column;
    gap: var(--field-gap-y);
}

.sph-fieldset__errors {
    margin-top: calc(var(--spacing) * 2);
}

/* label: as quiet as a field's label, with the controls as close to it as a field's control */
.sph-fieldset[data-variant='label'] {
    --fieldset-header-gap: calc(var(--spacing) * 2);
    --fieldset-description-gap: calc(var(--spacing) * 2);

    .sph-fieldset__legend {
        @apply text-base leading-snug font-medium;
    }

    .sph-fieldset__content {
        gap: calc(var(--spacing) * 3);
    }
}

.sph-fieldset[data-disabled] .sph-fieldset__legend {
    opacity: 0.5;
}

/*
 * side: a query cannot restyle its own container, so the fieldset is always the grid, and its parts
 * span both columns until there is room for the legend beside them. The rows then are
 *   1 legend | content · 2 description under the content · 3 errors
 */
.sph-fieldset[data-layout='side'] {
    --fieldset-line: var(--input-height);

    container: sph-fieldset / inline-size;
    display: grid;
    grid-template-columns: var(--form-field-label-width) minmax(0, 1fr);
    column-gap: var(--field-gap-x);

    > * {
        grid-column: 1 / -1;
    }
}

/* A choice's line is its checkbox, not an input. */
.sph-fieldset[data-layout='side']:has(> .sph-fieldset__content > [data-layout='choice']:first-child) {
    --fieldset-line: calc(var(--spacing) * 6);
}

/* The legend sits on the first control's line, as a side-labelled field's label does, with its help under the controls. */
@container sph-fieldset (width >= 30rem) {
    .sph-fieldset[data-layout='side'] {
        > .sph-fieldset__legend {
            grid-area: 1 / 1;
            margin-bottom: 0;
            padding-top: max(0px, calc((var(--fieldset-line) - 1lh) / 2));
        }

        > .sph-fieldset__content {
            grid-area: 1 / 2;
        }

        > .sph-fieldset__description {
            grid-area: 2 / 2;
            margin: calc(var(--spacing) * 1.5) 0 0;
        }

        > .sph-fieldset__errors {
            grid-area: 3 / 2;
        }
    }
}
</style>
